/* ==========================================================================
   TSH · Wishlist — núcleo
   The Sole Hunters · Tema Tinker (Horizon)
   --------------------------------------------------------------------------
   Capa única de acceso a los datos. Hoy guarda en localStorage; el día que
   haya backend solo cambian read() y write(), y nada más se entera.

   API pública (window.TshWishlist):
     .get()            → array de productos guardados
     .has(id)          → true si está guardado
     .add(producto)    → añade
     .remove(id)       → quita
     .toggle(producto) → alterna, devuelve true si quedó guardado
     .count()          → cuántos hay
     .clear()          → vacía

   Eventos que emite en document:
     tsh:wishlist:changed  → { items, count }
   ========================================================================== */

(function () {
  'use strict';

  if (window.TshWishlist) return;

  var KEY = 'tsh:wishlist';
  var MAX = 100;
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ------------------------------------------------------------------------
     ALMACENAMIENTO
     El único punto que toca localStorage. Sustituir esto por llamadas al
     backend es todo lo que hará falta el día que exista.
     ------------------------------------------------------------------------ */
  function read() {
    try {
      var raw = localStorage.getItem(KEY);
      var arr = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(arr)) return [];

      // Migración: las versiones antiguas guardaban solo IDs sueltos
      return arr
        .map(function (it) {
          if (typeof it === 'string' || typeof it === 'number') {
            return { id: String(it), title: '', url: '', image: '', price: '', added: 0 };
          }
          return it;
        })
        .filter(function (it) { return it && it.id; });
    } catch (e) {
      return [];
    }
  }

  function write(items) {
    try {
      localStorage.setItem(KEY, JSON.stringify(items.slice(0, MAX)));
      return true;
    } catch (e) {
      console.error('[TSH wishlist] no se pudo guardar', e);
      return false;
    }
  }

  function announce() {
    var items = read();
    document.dispatchEvent(new CustomEvent('tsh:wishlist:changed', {
      bubbles: true,
      detail: { items: items, count: items.length }
    }));
  }

  /* ------------------------------------------------------------------------
     API
     ------------------------------------------------------------------------ */
  var api = {
    get: read,

    count: function () { return read().length; },

    has: function (id) {
      var target = String(id);
      return read().some(function (it) { return String(it.id) === target; });
    },

    add: function (product) {
      if (!product || !product.id) return false;

      var items = read();
      var id = String(product.id);
      if (items.some(function (it) { return String(it.id) === id; })) return false;

      // El más reciente primero
      items.unshift({
        id: id,
        handle: product.handle || '',
        title: product.title || '',
        url: product.url || '',
        image: product.image || '',
        price: product.price || '',
        compare: product.compare || '',
        vendor: product.vendor || '',
        available: product.available !== false,
        variant: product.variant || '',
        added: Date.now()
      });

      write(items);
      announce();
      return true;
    },

    remove: function (id) {
      var target = String(id);
      var items = read();
      var next = items.filter(function (it) { return String(it.id) !== target; });

      if (next.length === items.length) return false;

      write(next);
      announce();
      return true;
    },

    toggle: function (product) {
      if (!product || !product.id) return false;
      if (api.has(product.id)) { api.remove(product.id); return false; }
      api.add(product);
      return true;
    },

    clear: function () {
      write([]);
      announce();
    },

    /* ----------------------------------------------------------------------
       REFRESCO SILENCIOSO
       Los datos guardados envejecen: el precio puede cambiar o el producto
       agotarse. Esto los actualiza en segundo plano sin bloquear nada.
       ---------------------------------------------------------------------- */
    refresh: function () {
      var items = read();
      var withHandle = items.filter(function (it) { return it.handle; });
      if (!withHandle.length) return Promise.resolve(items);

      return Promise.all(
        withHandle.map(function (it) {
          return fetch(ROOT + 'products/' + it.handle + '.js')
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; });
        })
      ).then(function (results) {
        var fresh = read();
        var changed = false;

        results.forEach(function (data, i) {
          if (!data) return;
          var handle = withHandle[i].handle;
          var entry = fresh.filter(function (f) { return f.handle === handle; })[0];
          if (!entry) return;

          var v = data.variants && data.variants[0];
          var newPrice = v ? formatMoney(v.price) : entry.price;

          if (entry.price !== newPrice || entry.available !== data.available) {
            entry.price = newPrice;
            entry.available = data.available;
            entry.title = data.title || entry.title;
            changed = true;
          }
        });

        if (changed) { write(fresh); announce(); }
        return fresh;
      });
    }
  };

  function formatMoney(cents) {
    try {
      var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
      var loc = (window.Shopify && window.Shopify.locale) || 'es-ES';
      return new Intl.NumberFormat(loc.indexOf('-') > -1 ? loc : 'es-ES', {
        style: 'currency', currency: cur
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' €';
    }
  }

  api.money = formatMoney;
  window.TshWishlist = api;

  /* ------------------------------------------------------------------------
     BOTONES
     Delegado en document: funciona con tarjetas cargadas después,
     paginación infinita, filtros de colección, lo que sea.
     ------------------------------------------------------------------------ */
  function paintButton(btn) {
    var saved = api.has(btn.dataset.wishId);
    btn.classList.toggle('is-saved', saved);
    btn.setAttribute('aria-pressed', String(saved));

    var label = btn.dataset[saved ? 'labelOn' : 'labelOff'];
    var text = btn.querySelector('[data-wish-label]');
    if (text && label) text.textContent = label;

    var aria = saved ? 'Quitar de la wishlist' : 'Guardar en la wishlist';
    btn.setAttribute('aria-label', aria);
  }

  function paintAll() {
    document.querySelectorAll('[data-wish-toggle]').forEach(paintButton);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wish-toggle]');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    var product = {
      id: btn.dataset.wishId,
      handle: btn.dataset.wishHandle,
      title: btn.dataset.wishTitle,
      url: btn.dataset.wishUrl,
      image: btn.dataset.wishImage,
      price: btn.dataset.wishPrice,
      compare: btn.dataset.wishCompare,
      vendor: btn.dataset.wishVendor,
      variant: btn.dataset.wishVariant,
      available: btn.dataset.wishAvailable !== 'false'
    };

    var saved = api.toggle(product);

    // Pulso solo al guardar: quitar no merece celebración
    if (saved) {
      btn.classList.remove('is-pulsing');
      void btn.offsetWidth;
      btn.classList.add('is-pulsing');
    }
  });

  document.addEventListener('tsh:wishlist:changed', paintAll);

  /* Compatibilidad con el hero, que emite el evento antiguo */
  document.addEventListener('tsh:wishlist:toggle', function (e) {
    var d = e.detail || {};
    if (!d.productId) return;
    if (d.active) api.add({ id: d.productId, title: d.title || '', url: d.url || '' });
    else api.remove(d.productId);
  });

  /* Sincroniza entre pestañas abiertas */
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) { paintAll(); announce(); }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', paintAll);
  } else {
    paintAll();
  }
})();